/**
 * Profile — the Profile tab.
 *
 * Single screen used by both regular users and admins; admin-only tools
 * appear in a separate section gated on `me.is_admin`. Wireframe-locked:
 *   - avatar + name + email
 *   - status pills (verified email, KYC level)
 *   - wallet shortcut card
 *   - "Settings" row list (interests, KYC, bank accounts, change password)
 *   - "Admin" section (Question Studio, Live trivia) — admins only
 *   - log out button at the bottom
 *
 * Pulls /me on render so the freshest is_admin / kyc_level / interests are
 * shown (and cached for the rest of the app).
 */

import { logout } from '../actions.js';
import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { icon, I } from '../components/icons.js';
import { toast } from '../components/toast.js';
import { requestHomeTourReplay, requestPowerupsTourReplay } from '../lib/tour.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';

export async function renderProfile(root) {
  const cached = auth.getMe();
  root.innerHTML = `
    <div class="appbar">
      <div class="appbar-title">Profile</div>
      <div class="appbar-spacer"></div>
      <a href="/me" data-link class="link-button" style="font-size:12.5px;">Edit</a>
    </div>
    <div class="pad" id="profile-page">
      ${cached ? renderBody(cached, null) : `<div class="skel"></div><div class="skel"></div>`}
    </div>
  `;

  // Always refresh from /me so the badge + interests + admin flag are current.
  let me;
  try {
    me = await api('/me');
    auth.setMe(me);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    if (cached) {
      toast.warn('Showing cached profile — could not refresh.');
      wire(cached);
      return;
    }
    toast.fromError(err, 'Could not load profile.');
    document.getElementById('profile-page').innerHTML =
      `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
    return;
  }

  // Wallet read is best-effort; if it fails we just show "—".
  let balance = null;
  try {
    const w = await api('/wallet');
    balance = w.balance_kobo;
  } catch { /* ignore */ }

  document.getElementById('profile-page').innerHTML = renderBody(me, balance);
  wire(me);
}

function avatarInitial(me) {
  return (me.username || me.email || '?').slice(0, 1).toUpperCase();
}

function statusBadges(me) {
  const verified = me.email_verified
    ? '<span class="status-pill ok">✓ email</span>'
    : '<span class="status-pill warn">unverified</span>';
  const kyc = me.kyc_level >= 2
    ? '<span class="status-pill ok">✓ KYC L2</span>'
    : me.kyc_level >= 1
      ? `<span class="status-pill warn">KYC L${me.kyc_level}</span>`
      : '<span class="status-pill warn">KYC pending</span>';
  const admin = me.is_admin
    ? '<span class="status-pill ok" style="background:#251E48;color:#B7A6FF;">ADMIN</span>'
    : '';
  return `${verified} ${kyc} ${admin}`;
}

function renderBody(me, balance) {
  return `
    <div class="card center">
      <div class="av xl" style="margin:0 auto 12px;">${escape(avatarInitial(me))}</div>
      <div style="font-family:var(--font-display);font-size:18px;font-weight:600;color:#fff;">
        ${escape(me.username || me.email.split('@')[0])}
      </div>
      <div style="font-size:12px;color:var(--mute);font-weight:500;margin-top:2px;">
        ${escape(me.email)}
      </div>
      <div style="margin-top:11px;display:flex;justify-content:center;flex-wrap:wrap;gap:6px;">
        ${statusBadges(me)}
      </div>
    </div>

    <a href="/wallet" data-link class="card" style="text-decoration:none;display:block;">
      <div class="row">
        <div class="ic gold">${icon(I.wallet, { size: 16 })}</div>
        <div class="grow">
          <b style="font-size:13px;color:#fff;font-weight:600;">Wallet balance</b>
          <div style="font-size:11px;color:var(--mute);font-weight:500;">
            Deposit, withdraw, history
          </div>
        </div>
        <b style="font-family:var(--font-display);color:var(--gold-t);font-weight:700;font-size:16px;">
          ${balance == null ? '—' : escape(formatKobo(balance))}
        </b>
      </div>
    </a>

    <h3 class="sec">Earn power-ups</h3>
    ${rowLink('/invite', I.star, 'Invite & Earn', `Refer friends · ${me.fiftyfifty_balance || 0} × 50/50 in inventory`)}

    <h3 class="sec">Your public profile</h3>
    ${rowLink(`/u/${encodeURIComponent(me.id)}`, I.friends, 'View as others see you', 'Stats, honours, privacy controls')}
    ${rowLink(`/u/${encodeURIComponent(me.id)}?tab=duels`, I.swords, '1-v-1 history', 'Every finished duel, topic, opponent, earnings')}

    <h3 class="sec">Account</h3>
    ${rowLink('/interests', I.star, 'Topics & interests', (me.interests || []).length ? `${me.interests.length} picked` : 'Pick at least 3')}
    ${rowLink('/kyc', I.lock, 'Identity verification', me.kyc_level >= 2 ? 'Verified' : 'Required for paid contests')}
    ${rowLink('/wallet/banks', I.bank, 'Bank accounts', 'For withdrawals')}
    ${rowLink('/me', I.cog, 'Settings', 'Username, avatar, password')}

    ${me.is_admin ? `
      <h3 class="sec">Admin tools</h3>
      ${rowLink('/admin', I.refresh, 'Dashboard', 'Users &amp; question bank at a glance')}
      ${rowLink('/admin/questions', I.bolt, 'AI Question Studio', 'Generate &amp; auto-publish trivia with Claude')}
      ${rowLink('/admin/live', I.bell, 'Live trivia', 'Schedule or go live now')}
    ` : ''}

    <h3 class="sec">Help</h3>
    <button type="button" id="replay-tour-btn" class="card pad-sm" style="display:block;width:100%;text-align:left;background:var(--surf);border:none;padding:12px;cursor:pointer;">
      <div class="row">
        <div class="ic">${icon(I.star, { size: 16 })}</div>
        <div class="grow">
          <b style="font-size:13px;color:#fff;font-weight:600;">Replay the home tour</b>
          <div style="font-size:11px;color:var(--mute);font-weight:500;">A quick walkthrough of the home screen</div>
        </div>
        <span style="color:var(--mute);">${icon(I.chevronRight, { size: 18 })}</span>
      </div>
    </button>
    <button type="button" id="replay-powerups-tour-btn" class="card pad-sm" style="display:block;width:100%;text-align:left;background:var(--surf);border:none;padding:12px;cursor:pointer;margin-top:8px;">
      <div class="row">
        <div class="ic">${icon(I.bolt, { size: 16 })}</div>
        <div class="grow">
          <b style="font-size:13px;color:#fff;font-weight:600;">Replay the power-ups tour</b>
          <div style="font-size:11px;color:var(--mute);font-weight:500;">Erasers, 50/50, and notifications</div>
        </div>
        <span style="color:var(--mute);">${icon(I.chevronRight, { size: 18 })}</span>
      </div>
    </button>
    ${rowLink('/health', I.refresh, 'System health', 'Server, websockets, database')}

    <div class="card" style="margin-top:14px;">
      <button type="button" id="logout-btn" class="btn ghost" style="color:var(--pink-t);border-color:#5e1a36;">
        Log out
      </button>
    </div>

    <div class="fine-print">
      18+ · Play responsibly · Licensed operator<br>
      Member since ${escape(new Date(me.created_at).toLocaleDateString('en-NG', { dateStyle: 'medium' }))}
    </div>
  `;
}

function rowLink(href, iconPath, title, sub) {
  return `
    <a href="${href}" data-link class="card pad-sm" style="text-decoration:none;display:block;padding:12px;">
      <div class="row">
        <div class="ic">${icon(iconPath, { size: 16 })}</div>
        <div class="grow">
          <b style="font-size:13px;color:#fff;font-weight:600;">${title}</b>
          <div style="font-size:11px;color:var(--mute);font-weight:500;">${sub}</div>
        </div>
        <span style="color:var(--mute);">${icon(I.chevronRight, { size: 18 })}</span>
      </div>
    </a>
  `;
}

function wire(_me) {
  document.getElementById('logout-btn')?.addEventListener('click', () => {
    void logout();
    toast.info('Signed out.');
  });
  document.getElementById('replay-tour-btn')?.addEventListener('click', () => {
    // Stash a session-scoped replay flag, then navigate home. The home
    // page's paint flow consumes the flag and starts the tour once
    // anchors are mounted — avoids racing the /home fetch.
    void requestHomeTourReplay();
    navigate('/');
  });
  document.getElementById('replay-powerups-tour-btn')?.addEventListener('click', () => {
    void requestPowerupsTourReplay();
    navigate('/');
  });
}
